// US-07 / US-11 — /activity rediseñada como tabla densa (UC-11.08)
// Patrón: KPIs arriba + tabla densa + side panel de detalle al click en fila.
import { useCallback } from "react";
import { Bike, RefreshCw } from "lucide-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, EmptyState, KPIStat } from "@/components/ui/v2";
import { ActivityTable } from "@/components/activity/ActivityTable";
import { useActivities } from "@/hooks/useActivities";
import { useWeeklySummary } from "@/hooks/useWeeklySummary";
import { useOAuthPopup, type OAuthPopupMessage } from "@/hooks/useOAuthPopup";
import { useSyncStrava } from "@/hooks/useSyncStrava";
import { supabase } from "@/lib/supabase";
import { env } from "@/lib/env";
import type { NormalisedActivity } from "@/hooks/useLastActivity";

// ─── Route search schema ─────────────────────────────────────────────────────

const activitySearchSchema = z.object({
  expandedId: z.coerce.number().int().positive().optional(),
});

type ActivitySearch = z.infer<typeof activitySearchSchema>;

export const Route = createFileRoute("/_authenticated/activity")({
  component: ActivityView,
  validateSearch: activitySearchSchema,
});

// ─── OAuth helpers ────────────────────────────────────────────────────────────

const HQ_API_ORIGIN = new URL(env.VITE_API_BASE_URL).origin;

async function buildStravaStartUrl(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) {
    toast.error("Inicia sesión antes de conectar Strava.");
    return null;
  }
  return `${HQ_API_ORIGIN}/oauth/strava/start?token=${encodeURIComponent(token)}`;
}

// ─── Format helpers ───────────────────────────────────────────────────────────

function formatDurationS(seconds: number): string {
  if (seconds <= 0) return "0s";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function deltaPct(ratio: number): { value: number; suffix?: string } | undefined {
  if (!Number.isFinite(ratio)) return undefined;
  if (ratio === 0) return undefined;
  return { value: Math.round(ratio * 100) };
}

// ─── Skeletons ────────────────────────────────────────────────────────────────

function KpiSkeleton() {
  return (
    <div className="rounded-lg border border-default bg-surface p-5 flex flex-col gap-2 animate-pulse">
      <div className="h-3 w-16 rounded bg-subtle" />
      <div className="h-6 w-24 rounded bg-subtle" />
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

function ActivityView() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/activity" });

  const activities = useActivities({ limit: 100 });
  const summary = useWeeklySummary();
  const { sync, isPending: isSyncing, cooldownLeftMs } = useSyncStrava();
  const cooldownLeftS = Math.ceil(cooldownLeftMs / 1000);

  const isLoading = activities.isLoading || summary.isLoading;
  const list = activities.data ?? [];
  const hasActivities = list.length > 0;

  // Toggle: si ya está expandida → colapsa; si no → expande.
  const onRowToggle = useCallback(
    (activity: NormalisedActivity) => {
      navigate({
        search: (prev: ActivitySearch) => ({
          ...prev,
          expandedId:
            prev.expandedId === activity.id ? undefined : activity.id,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const onPopupMessage = useCallback(
    (msg: OAuthPopupMessage) => {
      if (msg.ok) {
        toast.success("Strava conectado. Sincronizando actividades…");
        sync({ days: 90 });
      } else {
        toast.error(msg.detail ?? "No se pudo conectar Strava.");
      }
    },
    [sync],
  );

  const { open } = useOAuthPopup({
    expectedOrigin: HQ_API_ORIGIN,
    onMessage: onPopupMessage,
  });

  const handleConnectStrava = async () => {
    const url = await buildStravaStartUrl();
    if (url) open(url);
  };

  // ── Estado vacío ──────────────────────────────────────────────────────────────
  if (!isLoading && !hasActivities) {
    return <EmptyActivityState onConnectStrava={handleConnectStrava} />;
  }

  const s = summary.data;

  return (
    <div className="flex flex-col gap-6" data-testid="activity-page">
      {/* Page header */}
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-fg">Actividad</h1>
          <p className="text-sm text-fg-secondary mt-1">
            <span className="tabular font-mono">{isLoading ? "…" : list.length}</span> actividades ·
            Tu actividad deportiva (Strava) en una pantalla.
          </p>
        </div>
        <button
          type="button"
          onClick={() => sync({ days: 30 })}
          disabled={isSyncing || cooldownLeftS > 0}
          title={
            cooldownLeftS > 0
              ? `Espera ${cooldownLeftS}s antes del próximo intento`
              : "Sincroniza las últimas actividades desde Strava"
          }
          data-testid="activity-sync-button"
          className="inline-flex items-center gap-2 h-8 px-3 rounded-md text-sm font-medium border border-default text-fg hover:bg-subtle transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw
            className={`size-4 ${isSyncing ? "animate-spin" : ""}`}
            strokeWidth={1.5}
            aria-hidden
          />
          {isSyncing
            ? "Sincronizando…"
            : cooldownLeftS > 0
              ? `Sincronizar (${cooldownLeftS}s)`
              : "Sincronizar"}
        </button>
      </header>

      {/* KPI Strip — 4 KPIs de la semana actual */}
      <section className="grid grid-cols-12 gap-3" aria-label="KPIs actividad semanal">
        {isLoading ? (
          <>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="col-span-3">
                <KpiSkeleton />
              </div>
            ))}
          </>
        ) : s ? (
          <>
            <div className="col-span-3">
              <KPIStat
                label="Kilómetros"
                value={
                  <span className="font-mono tabular-nums">
                    {s.current.distanceKm.toFixed(1)}
                    <span className="text-sm font-normal text-fg-secondary ml-1">km</span>
                  </span>
                }
                delta={deltaPct(s.deltaDistancePct)}
                hint="esta semana"
              />
            </div>
            <div className="col-span-3">
              <KPIStat
                label="Tiempo total"
                value={
                  <span className="font-mono tabular-nums">
                    {formatDurationS(s.current.durationS)}
                  </span>
                }
                delta={deltaPct(s.deltaDurationPct)}
                hint="esta semana"
              />
            </div>
            <div className="col-span-3">
              <KPIStat
                label="Ascenso"
                value={
                  <span className="font-mono tabular-nums">
                    {s.current.elevationM.toFixed(0)}
                    <span className="text-sm font-normal text-fg-secondary ml-1">m</span>
                  </span>
                }
                delta={deltaPct(s.deltaElevationPct)}
                hint="esta semana"
              />
            </div>
            <div className="col-span-3">
              <KPIStat
                label="Salidas"
                value={
                  <span className="font-mono tabular-nums">{s.current.count}</span>
                }
                delta={deltaPct(s.deltaCountPct)}
                hint="esta semana"
              />
            </div>
          </>
        ) : null}
      </section>

      {/* Tabla densa con filas expandibles inline */}
      <Card padding="none" className="overflow-hidden">
        <CardHeader className="px-5 pt-5 pb-3">
          <CardTitle>Actividades</CardTitle>
          <span className="text-xs text-fg-secondary">
            Click en una fila para desplegar el detalle
          </span>
        </CardHeader>
        <ActivityTable
          activities={list}
          isLoading={isLoading}
          expandedId={search.expandedId ?? null}
          onRowToggle={onRowToggle}
        />
      </Card>
    </div>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function EmptyActivityState({ onConnectStrava }: { onConnectStrava: () => void }) {
  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-fg">Actividad</h1>
          <p className="text-sm text-fg-secondary mt-1">
            Tu actividad deportiva (Strava) en una pantalla.
          </p>
        </div>
      </header>

      {/* Ghost KPIs */}
      <div
        className="grid grid-cols-2 gap-3 sm:grid-cols-4 pointer-events-none select-none"
        aria-hidden
      >
        {[
          { label: "Kilómetros", value: "—" },
          { label: "Tiempo total", value: "—" },
          { label: "Ascenso", value: "—" },
          { label: "Salidas", value: "—" },
        ].map((k) => (
          <div
            key={k.label}
            className="rounded-lg border border-default bg-surface p-5 opacity-30 backdrop-blur-sm"
          >
            <p className="text-xs uppercase text-fg-secondary">{k.label}</p>
            <p className="text-xl font-semibold font-mono tabular-nums text-fg mt-1">
              {k.value}
            </p>
          </div>
        ))}
      </div>

      <Card padding="lg">
        <EmptyState
          icon={Bike}
          title="Conecta Strava"
          description="Cuando autorices Strava, esta vista mostrará tu actividad deportiva con métricas integradas: km semana, ritmo medio, elevación, última salida."
          cta={{
            label: "Conectar Strava",
            onClick: onConnectStrava,
            disabled: false,
          }}
        />
      </Card>
    </div>
  );
}
